import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Formik } from "formik";
import _ from "lodash";
// eslint-disable-next-line import/no-named-default
import { default as MomentLib } from "moment";
import { extendMoment } from "moment-range";
import {
  Actionsheet,
  Box,
  Button,
  Center,
  HStack,
  Modal,
  Pressable,
  Spinner,
  Text,
  TextArea,
  useDisclose,
  useTheme,
  VStack,
} from "native-base";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SafeAreaView, ScrollView, ViewStyle } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { runOnJS } from "react-native-reanimated";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import CopyMealsToAnotherDayButton from "../components/CopyMealsToCurrentDayButton";
import CustomBrandingMobileScreenHeader from "../components/CustomBrandingMobileScreenHeader";
import EmptyDayActions from "../components/EmptyDayActions";
import FloatingButton from "../components/FloatingButton";
import MealSlotInPlanner from "../components/MealSlotInPlanner";
import MenstrualCyclePhaseIndicator from "../components/MenstrualCyclePhaseIndicator";
import NoteComponent from "../components/NoteComponent";
import { MemoizedNutritionOverviewForTheDay } from "../components/NutritionOverview";
import QuickAddModal from "../components/QuickAddModal";
import ScreenContainer from "../components/ScreenContainer";
// import CalendarStripComponent from "../components/SlidingCalendar";
// Temporarily commented for custom implementation
import ViewAsUserHeader from "../components/ViewAsUserHeader";
import { commonStyles, isDesktopScreen, Routes } from "../constants";
import {
  addMealToPlanner,
  addSingleFoodMealToPlanner,
  createCalendarDays,
  findLastDateThereAreMealsPlannedWithThisNutritionDayPlan,
  generateMealsPrefetchOptions,
  getActualValueForMacro,
  getDayOfWeekAsString,
  getPlannerData,
  getTargetValueForMacro,
} from "../helpers/diaryHelpers";
import { FeatureFlag as FeatureFlagProper, isFeatureFlagActive } from "../helpers/featureFlags";
import {
  areTwoCalendarItemsArraysEqual,
  formatMealType,
  formatMomentAsBackendFormatDateString,
} from "../helpers/generalHelpers";
import {
  areWeAnOpinionatedRecipeInspirationApp,
  doesTheUserHaveMenstrualPlanSet,
  FeatureFlag,
  getOrganisation,
  isFeatureFlagEnabled,
  isSimpleOrg,
  shouldB2CCustomerHavePaidAccess,
} from "../helpers/userHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import type {
  CalendarDay,
  CalendarItem,
  Food,
  FoodGenerateMealsListApiArg,
  MealMomentEnum,
  MealSlotSpecification,
  MealTypeEnum,
  Organisation,
  SuggestedServing,
} from "../services/backendTypes";
import logger from "../services/logger";
import { calendarDaysSelector, currentDayInPlannerSelector, plannerSlice } from "../slices/plannerSlice";
import {
  localeSelector,
  userSlice,
  viewAsUserSelector,
  viewAsUserWeeklyNutritionPlanSelector,
  weeklyNutritionPlanSelector,
} from "../slices/userSlice";
import { CopyDayModal } from "./CopyDayModal";
import { CopyMealsModal } from "./CopyMealsModal";

const {
  useFoodQuickAddMealCreateMutation,
  usePlannerCalendarItemCreateMutation,
  // NOTE: There is a scoping problem with usePrefetch, I don't know why
  // eslint-disable-next-line @typescript-eslint/unbound-method
  usePrefetch,
  usePlannerCalendarDayPartialUpdateMutation,
  usePlannerPlanSingleFoodMealCreateMutation,
  usePlannerAutoPlanDayCreateMutation,
  usePlannerCopyMealsFromDateCreateMutation,
  usePlannerAutoplanSimpleRecipesCreateMutation,
} = backendApi;

// NOTE: The library has a typescript error in it which we cannot fix
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const moment = extendMoment(MomentLib);

const PREFETCH_TTL_IN_MINS = 5;

const noteSchema = Yup.object().shape({
  // NOTE: A workaround for this issue: https://github.com/jquense/yup/issues/1367
  note: Yup.string().trim().required().nullable(false) as Yup.StringSchema<string>,
});
type NoteFormSchema = Yup.InferType<typeof noteSchema>;

const MemoizedMealSlotInPlanner = React.memo(MealSlotInPlanner, (prevProps, nextProps) =>
  Boolean(
    prevProps.mealSlotSpecification.id === nextProps.mealSlotSpecification.id &&
      prevProps.calendarItems?.length === nextProps.calendarItems?.length &&
      prevProps.currentCalendarDay === nextProps.currentCalendarDay &&
      prevProps.calendarItems &&
      nextProps.calendarItems &&
      areTwoCalendarItemsArraysEqual(prevProps.calendarItems, nextProps.calendarItems)
  )
);

type Props = NativeStackScreenProps<RootStackParamList, Routes.DiaryViewScreen>;

const DiaryViewScreen = ({ navigation, route }: Props): JSX.Element => {
  const { t } = useTranslation();

  const getOrganisationForBrandingPreview = route?.params?.getOrganisationForBrandingPreview;

  const theme = useTheme();

  const dispatch = useDispatch();
  const isDesktop = isDesktopScreen();

  const [createCalendarItem] = usePlannerCalendarItemCreateMutation();
  const [createQuickAddMealBackendCall] = useFoodQuickAddMealCreateMutation();
  const [partialUpdateCalendarDayBackendCall, { isLoading: isLoadingPartialUpdateCalendarDay }] =
    usePlannerCalendarDayPartialUpdateMutation();
  const [planSingleFoodMealBackendCall] = usePlannerPlanSingleFoodMealCreateMutation();
  const [autoPlanDay, { isLoading: isLoadingAutoPlanDay }] = usePlannerAutoPlanDayCreateMutation();
  const [autoPlanSimpleRecipes, { isLoading: isLoadingAutoPlanSimpleRecipes }] =
    usePlannerAutoplanSimpleRecipesCreateMutation();
  const [copyFromDate, { isLoading: isLoadingCopyFromDate }] = usePlannerCopyMealsFromDateCreateMutation();

  const [mealSlotForActionSheetItems, setMealSlotForActionSheetItems] = useState<MealMomentEnum>("BREAKFAST");

  const { isOpen: isOpenQuickAdd, onOpen: onOpenQuickAdd, onClose: onCloseQuickAdd } = useDisclose();
  const { isOpen: isOpenNoteDialog, onOpen: onOpenNoteDialog, onClose: onCloseNoteDialog } = useDisclose();
  const { isOpen: isOpenCopyMealsTo, onOpen: onOpenCopyMealsTo, onClose: onCloseCopyMealsTo } = useDisclose();
  const { isOpen: isOpenCopyDay, onOpen: onOpenCopyDay, onClose: onCloseCopyDay } = useDisclose();
  const {
    isOpen: isOpenMealSlotAddMore,
    onOpen: onOpenMealSlotAddMore,
    onClose: onCloseMealSlotAddMore,
  } = useDisclose();

  const viewAsUser = useSelector(viewAsUserSelector);
  const weeklyNutritionPlan = useSelector(
    viewAsUser ? viewAsUserWeeklyNutritionPlanSelector : weeklyNutritionPlanSelector
  );

  const currentDayInPlanner = useSelector(currentDayInPlannerSelector);

  const calendarDaysInStore = useSelector(calendarDaysSelector);

  const userLocaleFromStore = useSelector(localeSelector);

  // TODO: Refactor the below to make it more readable
  const {
    refetchCalendarDays,
    calendarDayListResponse,
    plannerArgs,
    createNewCalendarDay,
    user,
    isLoadingCreateNewCalendarDay,
    isLoadingCalendarDayList,
    isFetchingCalendarDayList,
  } = getPlannerData();

  // Put the returned data for the CalendarDays in the store
  _.forEach(calendarDayListResponse?.results, (calendarDay) => {
    dispatch(plannerSlice.actions.storeCalendarDay(calendarDay));
  });

  // TODO: Prefetch USER_GENERATED meals search
  const foodGenerateMealsListPrefetch = usePrefetch("foodGenerateMealsList");
  const simpleRecipeSuggestionsPrefetch = usePrefetch("foodSimpleRecipeSuggestionsRetrieve");

  useEffect(() => {
    // NOTE: Do not to call this on every time appLastOpenedDateString is updated - this will trigger an infinite loop
    dispatch(userSlice.actions.storeAppLastOpened(moment().toISOString()));
  }, [dispatch]);

  const currentCalendarDay = calendarDaysInStore[formatMomentAsBackendFormatDateString(currentDayInPlanner)];

  const day = getDayOfWeekAsString(currentDayInPlanner);
  if (weeklyNutritionPlan && !(day in weeklyNutritionPlan)) {
    throw new Error(`"${day}" not found in weekly nutrition plan`);
  }
  const nutritionPlanForCurrentDay = _.get(weeklyNutritionPlan, day);

  // NOTE: We need to find all MealSlotSpecification (MSS) objects with the same meal_moment as
  // there can be multiple (same) MSS due to when the NutritionDayPlan (NDP)
  // is updated it will result in entirely new MSS objects. Doing it this way prevents CalendarItems from being lost
  const getCalendarItemsForSlot = (
    mealSlotSpecification: MealSlotSpecification,
    calendarDay: CalendarDay
  ): CalendarItem[] => _.filter(calendarDay.calendar_items, { meal_moment: mealSlotSpecification.meal_moment });

  let nutritionOverviewForTheDay = null;

  let orderedMealSlotsForTheDay: MealSlotSpecification[] = [];
  if (currentCalendarDay && nutritionPlanForCurrentDay) {
    if (!user) {
      logger.warn("No user found");
    } else if (isSimpleOrg(user)) {
      // Prefetch SimpleRecipe suggestions for SIMPLE organizations
      const createSimpleRecipeArgs = (
        mss: MealSlotSpecification
      ): { page: number; mealTypes: ("BREAKFAST" | "LUNCH" | "DINNER" | "SNACK")[] } => ({
        page: 1,
        // Use proper array format for suggestions endpoint
        mealTypes: [mss.meal_type as "BREAKFAST" | "LUNCH" | "DINNER" | "SNACK"],
        // Note: Suggestions endpoint auto-filters by user's dietary preferences & allergens
      });

      const createSimpleRecipePrefetchPromise = (mss: MealSlotSpecification): Promise<void> =>
        Promise.resolve(simpleRecipeSuggestionsPrefetch(createSimpleRecipeArgs(mss), generateMealsPrefetchOptions));

      const prefetchPromises = nutritionPlanForCurrentDay.meal_slot_specifications.map(
        createSimpleRecipePrefetchPromise
      );

      Promise.all(prefetchPromises).catch((e) => {
        logger.warn("Error prefetching foodSimpleRecipeList", e);
      });
    } else {
      // Existing complex recipe prefetch logic
      const createGenerateMealsArgs = (mss: MealSlotSpecification): FoodGenerateMealsListApiArg => ({
        mealSlotSpecification: mss.id,
        ingredientSearch: "",
        favourite: false,
        sourceProvider: ["WEEKMEALS"],
        tags: [],
        page: 1,
        user: user.id,
        mealTypes: [mss.meal_type],
        kcal: mss.kcal || 0, // NOTE: this included only for cache busting
        protein: mss.protein || 0, // NOTE: this is included only for cache busting
      });

      const createGenerateMealsPrefetchPromise = (mss: MealSlotSpecification): Promise<void> =>
        Promise.resolve(foodGenerateMealsListPrefetch(createGenerateMealsArgs(mss), generateMealsPrefetchOptions));

      const prefetchPromises = nutritionPlanForCurrentDay.meal_slot_specifications.map(
        createGenerateMealsPrefetchPromise
      );

      Promise.all(prefetchPromises).catch((e) => {
        logger.warn("Error prefetching foodGenerateMealsList", e);
      });
    }

    // TODO: Put this in a helper
    const MEAL_MOMENT_TO_ORDERING: { [key in MealMomentEnum]: number } = {
      BREAKFAST: 0,
      MORNING_SNACK: 1,
      LUNCH: 2,
      AFTERNOON_SNACK: 3,
      DINNER: 4,
      SNACK: 5,
      LATE_SNACK: 6,
    };

    const orderMealSlotByMealMoment = (mealSlotSpecification: MealSlotSpecification): number =>
      mealSlotSpecification.meal_moment ? MEAL_MOMENT_TO_ORDERING[mealSlotSpecification.meal_moment] : 100;

    orderedMealSlotsForTheDay = _.orderBy(
      nutritionPlanForCurrentDay.meal_slot_specifications,
      orderMealSlotByMealMoment
    );

    const getCalendarItemsForSlotCurried = (mealSlotSpecification: MealSlotSpecification): CalendarItem[] =>
      getCalendarItemsForSlot(mealSlotSpecification, currentCalendarDay);

    const calendarItemsForToday = _.map(
      nutritionPlanForCurrentDay.meal_slot_specifications,
      getCalendarItemsForSlotCurried
    ).flat();

    const actualKcalsForTheDay = getActualValueForMacro(calendarItemsForToday, "kcal");
    const actualProteinForTheDay = getActualValueForMacro(calendarItemsForToday, "protein");

    const targetKcalsForTheDay = getTargetValueForMacro(nutritionPlanForCurrentDay, "kcal");
    const targetProteinForTheDay = getTargetValueForMacro(nutritionPlanForCurrentDay, "protein");

    const actualFatForTheDay = getActualValueForMacro(calendarItemsForToday, "fat");
    const targetFatForTheDay = getTargetValueForMacro(nutritionPlanForCurrentDay, "fat");

    const actualCarbsForTheDay = getActualValueForMacro(calendarItemsForToday, "carbohydrates");
    const targetCarbsForTheDay = getTargetValueForMacro(nutritionPlanForCurrentDay, "carbohydrates");

    let shouldShowTargetMacros = true;
    if (user) {
      const organisation = getOrganisation(user || viewAsUser);
      if (organisation) {
        shouldShowTargetMacros =
          !isFeatureFlagActive(user, FeatureFlagProper.FF_RECIPE_INSPIRATION) &&
          !isFeatureFlagActive(user, FeatureFlagProper.FF_DONT_SHOW_MACROS);
      }
    }

    nutritionOverviewForTheDay =
      !isFeatureFlagActive(user, FeatureFlagProper.FF_RECIPE_INSPIRATION) &&
      !isFeatureFlagActive(user, FeatureFlagProper.FF_DONT_SHOW_MACROS) ? (
        <MemoizedNutritionOverviewForTheDay
          {...{
            actualKcalsForTheDay,
            actualProteinForTheDay,
            actualFatForTheDay,
            actualCarbsForTheDay,
            targetKcalsForTheDay,
            targetProteinForTheDay,
            targetFatForTheDay,
            targetCarbsForTheDay,
            t,
            shouldShowTargetMacros,
          }}
        />
      ) : null;
  }

  /** The if statement if(user || viewAsUser) does not prevent a type error
   * when calling getOrganisation. Only by assigning the variable does the type checker realise
   * that the result of If(user || viewAsUser) cannot be null. * */
  let organisation: Organisation | undefined;
  const orgUser = user || viewAsUser;
  if (orgUser) {
    organisation = getOrganisation(orgUser);
  }

  const findMealSlotSpecification = (mealMoment: MealMomentEnum): MealSlotSpecification | undefined =>
    nutritionPlanForCurrentDay
      ? _.find(nutritionPlanForCurrentDay.meal_slot_specifications, {
          meal_moment: mealMoment,
        })
      : undefined;

  const onPressPlanNewMeal = (mealMoment: MealMomentEnum): void => {
    const mealSlotSpecification = findMealSlotSpecification(mealMoment);
    if (!mealSlotSpecification) {
      throw new Error("Could not find meal slot specification");
    }

    if (user && isSimpleOrg(user)) {
      navigation.push(Routes.SimpleRecipeSearchScreen, {
        mealSlotSpecification,
        currentCalendarDay: currentCalendarDay!,
      });
    } else {
      navigation.push(Routes.AddRecipeTemplateStack, {
        // NOTE: We are probably defining the types wrong
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        screen: Routes.RecipeSearchScreen,
        params: { mealSlotSpecification, currentCalendarDay },
      });
    }
  };

  const createMealSlotInPlanner = (mealSlot: MealSlotSpecification): JSX.Element | null => {
    // NOTE: The same number of hooks must be created every time so the return statement must go after
    if (!currentCalendarDay) {
      return null;
    }

    const calendarItemsForMealSlot = getCalendarItemsForSlot(mealSlot, currentCalendarDay);
    let shouldShowTargetMacros = true;
    if (user) {
      if (organisation) {
        shouldShowTargetMacros =
          !isFeatureFlagActive(user, FeatureFlagProper.FF_RECIPE_INSPIRATION) &&
          !isFeatureFlagActive(user, FeatureFlagProper.FF_DONT_SHOW_MACROS);
      }
    }

    const handleCopyMealFromCard = (mealMoment: MealMomentEnum): void => {
      setMealSlotForActionSheetItems(mealMoment);
      onOpenCopyMealsTo();
    };

    const shouldHideMacros = isFeatureFlagActive(user, FeatureFlagProper.FF_DONT_SHOW_MACROS);

    return (
      <MemoizedMealSlotInPlanner
        key={mealSlot.meal_moment}
        actualValue={shouldHideMacros ? 0 : getActualValueForMacro(calendarItemsForMealSlot, "kcal")}
        targetValue={shouldHideMacros ? 0 : mealSlot.kcal ?? 0}
        calendarItems={calendarItemsForMealSlot}
        currentCalendarDay={currentCalendarDay}
        navigation={navigation}
        mealSlotSpecification={mealSlot}
        // TODO: Is this superfluous or are we going to do something with it?
        onPressPlannedStatus={(item) => logger.debug("onPress MealCategoryCard with item:", item)}
        refetchCalendarDays={refetchCalendarDays}
        onPressAddMoreComponent={() => {
          if (!mealSlot.meal_moment) throw new Error("meal_moment is not defined");

          setMealSlotForActionSheetItems(mealSlot.meal_moment);
          onOpenMealSlotAddMore();
        }}
        onPressPlanNewMeal={() => {
          if (!mealSlot.meal_moment) throw new Error("meal_moment is not defined");
          onPressPlanNewMeal(mealSlot.meal_moment);
        }}
        onPressMenu={() => {
          // This function is kept for compatibility but no longer used
        }}
        showTarget={shouldShowTargetMacros}
        onPressCopyMealFromCard={handleCopyMealFromCard}
      />
    );
  };

  const diaryComponent = _.map(orderedMealSlotsForTheDay, createMealSlotInPlanner);

  const noWeeklyNutritionPlanMessage = (
    <Center>
      <Text fontWeight="bold" fontSize="md" color="function.40">
        {t("planner.no_weekly_nutrition_plan_message")}
      </Text>
    </Center>
  );

  // FIXME: This should not be hardcoded but chosen based on logic for what time of day it is
  const getMealSlotForQuickButtons = (): MealSlotSpecification | undefined =>
    nutritionPlanForCurrentDay ? nutritionPlanForCurrentDay.meal_slot_specifications[0] : undefined;

  const addSingleFoodMealToPlannerWrapper = (
    food: Food,
    suggestedServing: SuggestedServing,
    quantity: number,
    mealSlot: MealSlotSpecification | undefined = getMealSlotForQuickButtons()
  ): Promise<void> => {
    if (!currentCalendarDay) {
      return Promise.reject(new Error("No current calendar day"));
    }
    if (!mealSlot) {
      return Promise.reject(new Error("No meal slot"));
    }

    return addSingleFoodMealToPlanner({
      food,
      suggestedServing,
      quantity,
      currentCalendarDay,
      mealSlot,
      refetchCalendarDays,
      dispatch,
      planSingleFoodMealBackendCall,
    });
  };

  const floatingButton = !isFeatureFlagEnabled(organisation || null, FeatureFlag.DisableProductLogging) ? (
    <FloatingButton
      navigation={navigation}
      addSingleFoodMealToPlanner={addSingleFoodMealToPlannerWrapper}
      displayQuickAddModal={onOpenQuickAdd}
      mealSlotSpecifications={nutritionPlanForCurrentDay?.meal_slot_specifications ?? undefined}
    />
  ) : null;
  const resetViewAsUser = (): void => {
    dispatch(plannerSlice.actions.resetCalendarDays());
    dispatch(userSlice.actions.setViewAsUser(null));
    navigation.goBack();
  };

  const createQuickAddMeal = async ({
    mealSlotSpecificationId,
    calories,
    protein,
    fat,
    carbohydrates,
  }: {
    mealSlotSpecificationId: number;
    calories: number;
    protein: number;
    fat: number;
    carbohydrates: number;
  }): Promise<void> => {
    if (!currentCalendarDay) {
      throw new Error("currentCalendarDay is not set");
    }

    onCloseQuickAdd();

    const quickAddMeal = await createQuickAddMealBackendCall({
      quickAddMealRequest: {
        kcal: Number(calories),
        protein: Number(protein),
        fat: Number(fat),
        carbohydrates: Number(carbohydrates),
      },
    }).unwrap();

    if (!nutritionPlanForCurrentDay) {
      throw new Error("nutritionPlanForCurrentDay is not set");
    }
    const mealSlot = _.find(nutritionPlanForCurrentDay.meal_slot_specifications, {
      id: Number(mealSlotSpecificationId),
    });

    if (!mealSlot) {
      throw new Error("Could not find meal slot");
    }

    await addMealToPlanner({
      currentCalendarDay,
      mealSlot,
      mealContentType: "quickaddmeal",
      meal: { ...quickAddMeal, content_type: "QuickAddMeal", id: quickAddMeal.id },
      createCalendarItem,
      refetchCalendarDays,
    });
  };

  const quickAddModal = nutritionPlanForCurrentDay ? (
    <Modal isOpen={isOpenQuickAdd} onClose={onCloseQuickAdd} size="xl">
      <Modal.Content bgColor={"white"}>
        <Modal.CloseButton />
        <Modal.Header fontSize="4xl" fontWeight="bold" bgColor={"white"}>
          {t("planner.quick_options.quick_add_title")}
        </Modal.Header>
        <Modal.Body bgColor={"white"}>
          <QuickAddModal
            mealSlotSpecifications={nutritionPlanForCurrentDay.meal_slot_specifications}
            createQuickAddMeal={createQuickAddMeal}
          />
        </Modal.Body>
      </Modal.Content>
    </Modal>
  ) : null;

  const copyMealsToModalComponent = (
    <Modal isOpen={isOpenCopyMealsTo} onClose={onCloseCopyMealsTo} size="xl">
      <Modal.Content bgColor={"white"}>
        <Modal.CloseButton testID="copyMealMoment-modal-close-button" />
        <Modal.Header fontSize="4xl" fontWeight="bold" bgColor={"white"}>
          {t("planner.copy_meals_to_modal.title", {
            mealMoment: formatMealType(mealSlotForActionSheetItems, t).toLowerCase(),
          })}
        </Modal.Header>
        <Modal.Body bgColor={"white"}>
          <CopyMealsModal
            user={user}
            onClose={onCloseCopyMealsTo}
            currentCalendarDay={currentCalendarDay}
            mealMoment={mealSlotForActionSheetItems}
          />
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );

  const addMoreComponent = (
    <Actionsheet isOpen={isOpenMealSlotAddMore} onClose={onCloseMealSlotAddMore}>
      <Actionsheet.Content backgroundColor="white">
        <Actionsheet.Item
          onPress={(): void => {
            onCloseMealSlotAddMore();
            onOpenQuickAdd();
          }}
          testID={"addMoreActionSheet-quickAdd-button"}
          backgroundColor="white"
          _pressed={{ backgroundColor: theme.colors.primary["600"] }}
        >
          {t("planner.quick_options.quick_add_title")}
        </Actionsheet.Item>
        <Actionsheet.Item
          onPress={() => {
            onCloseMealSlotAddMore();
            navigation.push(Routes.AddProductStack, {
              screen: Routes.FoodSearchScreen,
              params: {
                onChoose: async (product, suggestedServing, quantity) => {
                  if (!nutritionPlanForCurrentDay) {
                    throw new Error("nutritionPlanForCurrentDay is not set");
                  }

                  const mealSlotSpecification = findMealSlotSpecification(mealSlotForActionSheetItems);

                  if (!mealSlotSpecification) {
                    throw new Error("Could not find meal slot");
                  }

                  // NOTE: The below function is used in two places and is difficult to define before its use
                  // eslint-disable-next-line no-use-before-define
                  await addSingleFoodMealToPlannerWrapper(product, suggestedServing, quantity, mealSlotSpecification);

                  navigation.pop(1);
                },
              },
            });
          }}
          backgroundColor="white"
          _pressed={{ backgroundColor: theme.colors.primary["600"] }}
        >
          {t("planner.quick_options.add_product_title")}
        </Actionsheet.Item>
        <Actionsheet.Item
          onPress={() => {
            onCloseMealSlotAddMore();
            onPressPlanNewMeal(mealSlotForActionSheetItems);
          }}
          testID={"addMoreActionSheet-recipe-button"}
          backgroundColor="white"
          _pressed={{ backgroundColor: theme.colors.primary["600"] }}
        >
          {t("planner.quick_options.add_recipe_title")}
        </Actionsheet.Item>
      </Actionsheet.Content>
    </Actionsheet>
  );

  const copyDayModalComponent = (
    <Modal isOpen={isOpenCopyDay} onClose={onCloseCopyDay} size="xl">
      <Modal.Content bgColor={"white"}>
        <Modal.CloseButton testID="copyDay-modal-close-button" />
        <Modal.Header fontSize="4xl" fontWeight="bold" bgColor={"white"}>
          {t("planner.copy_meals_to_modal.copy_meals")}
        </Modal.Header>
        <Modal.Body bgColor={"white"}>
          <CopyDayModal user={user} onClose={onCloseCopyDay} currentCalendarDay={currentCalendarDay} />
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );

  const shouldShowPreviewedBrandingHeader = Boolean(getOrganisationForBrandingPreview);

  const organisationForPreview = shouldShowPreviewedBrandingHeader
    ? getOrganisationForBrandingPreview()
    : {
        id: 1,
        name: "PLACEHOLDER_ORG",
        product_stripe_id: "prod_1234",
        recipe_system_type: "COMPLEX",
        whitelisted_recipes: [],
        denylist_recipes: [],
        displayed_recipe_tags: [],
        featured_recipe_tag: null,
      };

  const showCustomBrandingHeader = false; // Hide logo from diary view screen

  // Week navigation functions for swipe gestures
  const goToPreviousWeek = (): void => {
    const prevWeek = currentDayInPlanner.clone().subtract(1, "week");
    dispatch(plannerSlice.actions.setCurrentDayInPlanner(prevWeek.format()));
  };

  const goToNextWeek = (): void => {
    const nextWeek = currentDayInPlanner.clone().add(1, "week");
    dispatch(plannerSlice.actions.setCurrentDayInPlanner(nextWeek.format()));
  };

  // Simplified calendar strip component
  const calendarStrip = React.useCallback(() => {
    // Generate week dates starting from Monday
    const startOfWeek = currentDayInPlanner.clone().startOf("isoWeek");
    const weekDates = [];
    for (let i = 0; i < 7; i += 1) {
      weekDates.push(startOfWeek.clone().add(i, "days"));
    }

    const monthName = currentDayInPlanner.format("MMMM");
    const isToday = currentDayInPlanner.isSame(new Date(), "day");
    const dayText = isToday ? t("planner.title.today") : currentDayInPlanner.format("dddd");

    // Pan gesture for swipe navigation
    const panGesture = Gesture.Pan().onEnd((e) => {
      if (Math.abs(e.translationX) > 50 || Math.abs(e.velocityX) > 300) {
        if (e.translationX > 0) {
          runOnJS(goToPreviousWeek)();
        } else {
          runOnJS(goToNextWeek)();
        }
      }
    });

    return (
      <Box>
        {/* Add top padding and horizontal padding to align with meal cards */}
        <Box px={2} pt={10} pb={6}>
          {/* Header with mixed typography */}
          <Box flexDirection="row" justifyContent="space-between" alignItems="center" mb={8}>
            <Box flexDirection="row" alignItems="baseline">
              <Text fontSize="40px" fontFamily={theme.fonts.body} color="function.50" lineHeight="48px">
                {t("planner.title.plan_for")}
              </Text>
              <Text> </Text>
              <Text
                fontSize="32px"
                fontFamily="PlayfairDisplay_700Bold"
                fontWeight="700"
                fontStyle="italic"
                color="#7E7E7E"
                lineHeight="48px"
              >
                {dayText}
              </Text>
            </Box>
          </Box>

          {/* Month name */}
          <Text fontSize="32px" fontFamily={theme.fonts.body} color="function.50" lineHeight="32px" mb={4}>
            {monthName}
          </Text>

          {/* Week dates */}
          <GestureDetector gesture={panGesture}>
            <HStack justifyContent="space-between" alignItems="center">
              {weekDates.map((date, index) => {
                const isSelected = date.isSame(currentDayInPlanner, "day");
                const dayName = date.format("ddd");
                const dayNumber = date.format("D");

                return (
                  <Pressable
                    key={index}
                    flex={1}
                    alignItems="center"
                    py={2}
                    onPress={() => dispatch(plannerSlice.actions.setCurrentDayInPlanner(date.format()))}
                  >
                    <VStack alignItems="center" space={2}>
                      <Text
                        fontSize="14px"
                        fontFamily={theme.fonts.body}
                        fontWeight={isSelected ? "500" : "400"}
                        color={isSelected ? "function.50" : "function.20"}
                      >
                        {dayName}
                      </Text>
                      <Text
                        fontSize="14px"
                        fontFamily={theme.fonts.body}
                        color={isSelected ? "function.50" : "function.20"}
                      >
                        {dayNumber}
                      </Text>
                      {/* Dot indicator for selected day */}
                      <Box
                        size="5px"
                        borderRadius="full"
                        bg={(() => {
                          if (isSelected) return "function.50";
                          if (date.isSame(new Date(), "day")) return "function.20";
                          return "transparent";
                        })()}
                      />
                    </VStack>
                  </Pressable>
                );
              })}
            </HStack>
          </GestureDetector>
        </Box>
      </Box>
    );
  }, [currentDayInPlanner, theme, t, dispatch, goToPreviousWeek, goToNextWeek]);

  const onPressGenerateCurrentDay = async (): Promise<void> => {
    if (!user) {
      throw new Error("user is not set");
    }

    // Check if SIMPLE organization
    if (isSimpleOrg(user)) {
      // Use SimpleRecipe auto-planning
      await autoPlanSimpleRecipes({
        autoPlanSimpleRecipesRequest: {
          start_date: formatMomentAsBackendFormatDateString(currentDayInPlanner),
          delete_existing_plans: false,
        },
      });
    } else {
      // Use complex recipe auto-planning
      await autoPlanDay({
        autoPlannerRequest: {
          date: formatMomentAsBackendFormatDateString(currentDayInPlanner),
          user: user.id,
        },
      });
    }
  };

  const onSubmitNote = async (values: NoteFormSchema): Promise<void> => {
    onCloseNoteDialog();

    if (!currentCalendarDay) {
      throw new Error("currentCalendarDay is not set");
    }

    if (!currentCalendarDay.id) {
      throw new Error("currentCalendarDay.id is not set");
    }

    await partialUpdateCalendarDayBackendCall({
      id: currentCalendarDay.id,
      patchedCalendarDayRequest: {
        note: values.note,
      },
    });
  };

  const noteDialog = (): JSX.Element => (
    <Modal isOpen={isOpenNoteDialog} onClose={onCloseNoteDialog} size="xl">
      <Modal.Content bgColor={"white"}>
        <Modal.CloseButton testID="note-modal-close-button" />
        <Modal.Header fontSize="4xl" fontWeight="bold" bgColor={"white"}>
          {t("planner.note_dialog.title")}
        </Modal.Header>
        <Modal.Body bgColor={"white"}>
          <Text
            style={{
              fontStyle: "italic",
            }}
            color="function.30"
          >
            {organisation?.custom_notes_message
              ? organisation?.custom_notes_message
              : t("planner.note_dialog.information_label")}
          </Text>

          <Formik
            initialValues={noteSchema.cast({
              note: currentCalendarDay?.note || "",
            })}
            validationSchema={noteSchema}
            onSubmit={onSubmitNote}
          >
            {({ handleSubmit, handleChange, values, dirty, isValid, isSubmitting }) => (
              <>
                <TextArea
                  autoCompleteType="off"
                  placeholder={t("planner.note_dialog.textarea_placeholder")}
                  placeholderTextColor="function.20"
                  color="function.40"
                  borderColor="function.20"
                  _focus={{ borderColor: "primary.400" }}
                  value={values.note}
                  onChangeText={handleChange("note")}
                  mt="3"
                  testID="note-textarea"
                />

                <Button
                  isDisabled={!dirty || !isValid}
                  isLoading={isSubmitting || isLoadingPartialUpdateCalendarDay}
                  onPress={() => handleSubmit()}
                  mt="3"
                  testID="note-submit-button"
                >
                  {t("planner.note_dialog.save_button")}
                </Button>
              </>
            )}
          </Formik>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );

  const hasMealsPlanned = (): boolean =>
    Boolean(currentCalendarDay && currentCalendarDay.calendar_items && currentCalendarDay.calendar_items.length > 0);

  const hasRecipeMealsPlanned = (): boolean => {
    if (!currentCalendarDay || !currentCalendarDay.calendar_items) return false;

    return currentCalendarDay.calendar_items.some((item) => item.content_type === "recipemeal");
  };

  const bodyIfCalendarDayExists = (
    <Box>
      <Box>
        <CopyMealsToAnotherDayButton
          hasMealsPlanned={hasMealsPlanned()}
          hasRecipeMealsPlanned={hasRecipeMealsPlanned()}
          user={user}
          onOpenCopyDay={onOpenCopyDay}
        />
      </Box>

      <Box>
        <NoteComponent currentCalendarDay={currentCalendarDay} user={user} onPressNote={onOpenNoteDialog} />
      </Box>

      <VStack space={1}>{diaryComponent}</VStack>
    </Box>
  );

  const onPressCreateEmptyDay = async (): Promise<void> => {
    if (!user || !calendarDayListResponse || !weeklyNutritionPlan) {
      throw new Error("user, calendarDayListResponse or weeklyNutritionPlan is not set");
    }

    await createCalendarDays(
      calendarDayListResponse,
      // No specific generic provided, which should be fine
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      dispatch,
      plannerArgs,
      createNewCalendarDay,
      user,
      weeklyNutritionPlan,
      isLoadingCreateNewCalendarDay,
      isLoadingCalendarDayList,
      calendarDaysInStore
    );
  };

  const lastDateThereAreMealsPlannedWithThisNutritionDayPlan = findLastDateThereAreMealsPlannedWithThisNutritionDayPlan(
    nutritionPlanForCurrentDay,
    weeklyNutritionPlan,
    currentDayInPlanner,
    calendarDaysInStore
  );

  const onPressCopyFromLastRelevantDate = async (): Promise<void> => {
    if (
      !lastDateThereAreMealsPlannedWithThisNutritionDayPlan ||
      !lastDateThereAreMealsPlannedWithThisNutritionDayPlan.id
    ) {
      throw new Error("lastDateThereAreMealsPlannedWithThisNutritionDayPlan is not set");
    }

    if (!user) {
      throw new Error("user is not set");
    }

    await copyFromDate({
      copyFromDateRequest: {
        user: user.id,
        from_calendar_day_id: lastDateThereAreMealsPlannedWithThisNutritionDayPlan.id,
        to_date: formatMomentAsBackendFormatDateString(currentDayInPlanner),
      },
    });
  };

  const bodyIfNoCalendarDay = (
    <EmptyDayActions
      user={user}
      organisation={organisation}
      onPressChooseForMe={onPressGenerateCurrentDay}
      onPressDoItMyself={onPressCreateEmptyDay}
      onPressCopyFromDate={onPressCopyFromLastRelevantDate}
      isLoadingChooseForMe={isLoadingAutoPlanDay || isLoadingAutoPlanSimpleRecipes}
      isLoadingDoItMyself={isLoadingCreateNewCalendarDay}
      isLoadingCopyFromDate={isLoadingCopyFromDate}
      lastDateThereAreMealsPlannedWithThisNutritionDayPlan={lastDateThereAreMealsPlannedWithThisNutritionDayPlan}
      userLocaleFromStore={userLocaleFromStore}
      calendarDayListResponse={calendarDayListResponse}
      weeklyNutritionPlan={weeklyNutritionPlan}
      nutritionPlanForCurrentDay={nutritionPlanForCurrentDay}
    />
  );

  // Add this useEffect hook to check the subscription when the screen loads
  useEffect(() => {
    const verifySubscription = (): void => {
      if (!shouldB2CCustomerHavePaidAccess(user)) {
        navigation.navigate(Routes.BillingIssueScreen);
      }
    };

    verifySubscription();
  }, [user]);

  // Prefetch SimpleRecipe search results when SimpleRecipeMeals are visible
  useEffect(() => {
    if (currentCalendarDay?.calendar_items && user && isSimpleOrg(user) && nutritionPlanForCurrentDay) {
      // Find all SimpleRecipeMeals in current day
      const simpleRecipeMeals = currentCalendarDay.calendar_items.filter(
        (item) => item.content_type === "simplerecipemeal"
      );

      if (simpleRecipeMeals.length > 0) {
        // Get unique meal types from the SimpleRecipeMeals by looking up their meal slot specifications
        const mealTypes = new Set(
          simpleRecipeMeals
            .map((item) => {
              // Find the meal slot specification for this calendar item
              const mealSlot = nutritionPlanForCurrentDay.meal_slot_specifications.find(
                (mss) => mss.meal_moment === item.meal_moment
              );
              return mealSlot?.meal_type;
            })
            .filter(
              (mealType): mealType is "BREAKFAST" | "LUNCH" | "DINNER" | "SNACK" =>
                mealType !== undefined &&
                (mealType === "BREAKFAST" || mealType === "LUNCH" || mealType === "DINNER" || mealType === "SNACK")
            )
        );

        // Prefetch for each unique meal type
        mealTypes.forEach((mealType) => {
          simpleRecipeSuggestionsPrefetch(
            {
              page: 1,
              // Match: searchText.length >= 3 ? searchText : undefined (initially undefined)
              search: undefined,
              // Use proper array format for suggestions endpoint
              mealTypes: [mealType],
              // Match: appliedTags.length > 0 ? appliedTags : undefined (initially undefined)
              customTags: undefined,
              // Match: appliedPrepTime !== "any" ? ... : undefined (initially undefined)
              totalTimeLte: undefined,
              // Match: appliedFavorites !== null ? appliedFavorites : undefined (initially undefined)
              isFavorite: undefined,
              // Note: Suggestions endpoint auto-filters by user's dietary preferences & allergens
            },
            { ifOlderThan: PREFETCH_TTL_IN_MINS * 60 }
          );
        });
      }
    }
  }, [currentCalendarDay?.calendar_items, nutritionPlanForCurrentDay, simpleRecipeSuggestionsPrefetch, user]);

  // Determine if we should show the loading spinner
  const showLoadingSpinner = (isLoadingCalendarDayList || isFetchingCalendarDayList) && !hasMealsPlanned();

  return (
    <ScreenContainer safeAreaMode="top-horizontal" backgroundColor="primary.10">
      {showLoadingSpinner ? ( // Use the combined loading state
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ width: "100%", paddingHorizontal: isDesktop ? "25%" : 10 }}
          contentContainerStyle={{ paddingBottom: 20 }}
          nativeID="diaryViewScreenView"
        >
          {showCustomBrandingHeader ? (
            <CustomBrandingMobileScreenHeader
              organisationForPreview={shouldShowPreviewedBrandingHeader ? organisationForPreview : undefined}
            />
          ) : null}
          <ViewAsUserHeader viewAsUser={viewAsUser} onPressBack={resetViewAsUser} />
          {calendarStrip()}
          <Center flex={1} pt={8}>
            <Spinner size="lg" color="primary.40" testID="diary-loading-spinner" />
          </Center>
        </ScrollView>
      ) : (
        <>
          {/* Wrap all content in ScrollView so header components scroll with content */}
          <ScrollView
            showsVerticalScrollIndicator={false}
            style={{ width: "100%", paddingHorizontal: isDesktop ? "25%" : 10 }}
            contentContainerStyle={{ paddingBottom: 20 }}
            nativeID="diaryViewScreenView"
          >
            {showCustomBrandingHeader ? (
              <CustomBrandingMobileScreenHeader
                organisationForPreview={shouldShowPreviewedBrandingHeader ? organisationForPreview : undefined}
              />
            ) : null}
            <ViewAsUserHeader viewAsUser={viewAsUser} onPressBack={resetViewAsUser} />
            {calendarStrip()}

            <Box>{!weeklyNutritionPlan ? noWeeklyNutritionPlanMessage : null}</Box>

            {nutritionOverviewForTheDay ? (
              <Box mb={2} mt={1} bg="transparent" px={2.5} py={4} borderRadius={theme.borderRadii.card.default}>
                {nutritionOverviewForTheDay}
              </Box>
            ) : null}

            {user && doesTheUserHaveMenstrualPlanSet(user) ? (
              <Box mb={4}>
                <MenstrualCyclePhaseIndicator user={user} date={currentDayInPlanner} />
              </Box>
            ) : null}

            {/* Generate meals component removed - now integrated into EmptyDayActions */}

            <Box mt={-2}>{!currentCalendarDay ? bodyIfNoCalendarDay : bodyIfCalendarDayExists}</Box>
          </ScrollView>
          {/* Modals and Floating Button remain outside the ScrollView */}
          {!weeklyNutritionPlan ? null : floatingButton}
          {quickAddModal}
          {addMoreComponent}
          {copyMealsToModalComponent}
          {copyDayModalComponent}
          {noteDialog()}
        </>
      )}
    </ScreenContainer>
  );
};

export default DiaryViewScreen;
